import { FunctionComponent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { DashboardWrapper, Card, Text } from 'src/shared';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { getJobStats } from 'src/services/api';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
`;

const MetricCard = styled(Card)`
    padding: 20px;
`;

const MetricValue = styled.div<{ $color?: string }>`
    font-size: 28px;
    font-weight: 700;
    color: ${p => p.$color || Colors.extra.black};
    margin-bottom: 2px;
`;

const MetricLabel = styled.div`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
`;

const MetricSub = styled.div`
    font-size: 11px;
    color: ${Colors.extra.dark_gray};
    margin-top: 4px;
`;

const SectionCard = styled(Card)`
    padding: 20px;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${Colors.extra.black};
    margin-bottom: 16px;
`;

const StatRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid ${Colors.extra.light_gray};
    &:last-child { border-bottom: none; }
`;

const StatLabel = styled.span`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
`;

const StatValue = styled.span<{ $color?: string }>`
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.$color || Colors.extra.black};
`;

const PipelineStatValue = styled(StatValue)`
    justify-self: end;
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

/** Fixed label + fluid bar + fixed % column so bars align across rows */
const PipelineStatRow = styled.div`
    display: grid;
    grid-template-columns: 7.5rem 1fr 3.25rem;
    gap: 12px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid ${Colors.extra.light_gray};
    &:last-child {
        border-bottom: none;
    }
`;

const BarBg = styled.div`
    height: 8px;
    min-width: 0;
    background: ${Colors.extra.light_gray};
    border-radius: 4px;
    overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $color: string }>`
    height: 100%;
    width: ${p => Math.min(100, p.$pct)}%;
    background: ${p => p.$color};
    border-radius: 4px;
    transition: width 0.3s ease;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const SubTitle = styled(Text)`
    color: ${Colors.extra.dark_gray};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface Stats {
    total_jobs: number;
    total_succeeded: number;
    total_failed: number;
    total_reviewed: number;
    total_unreviewed: number;
    completed_24h: number;
    completed_7d: number;
    failed_7d: number;
    reviewed_7d: number;
    total_patients: number;
    linked_jobs: number;
    unlinked_jobs: number;
    failure_rate_pct: number;
    review_rate_pct: number;
}

const AnalyticsPage: FunctionComponent = () => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await getJobStats();
                setStats(res);
            } catch { /* */ }
            finally { setLoading(false); }
        })();
    }, []);

    const s = stats;

    return (
        <DashboardWrapper>
            <Container>
                <div>
                    <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                        Analytics
                    </Text>
                    <SubTitle fontSize={Size.Small}>
                        Operational trends and pipeline health — derived from scan analysis data
                    </SubTitle>
                </div>

                {loading ? (
                    <Text color={Colors.extra.dark_gray}>Loading stats...</Text>
                ) : !s ? (
                    <Text color={Colors.extra.dark_gray}>Failed to load analytics data.</Text>
                ) : (
                    <>
                        {/* Top-level metrics */}
                        <MetricsGrid>
                            <MetricCard>
                                <MetricValue>{s.total_jobs}</MetricValue>
                                <MetricLabel>Total Scans</MetricLabel>
                                <MetricSub>All time</MetricSub>
                            </MetricCard>
                            <MetricCard>
                                <MetricValue $color="#059669">{s.total_succeeded}</MetricValue>
                                <MetricLabel>Completed</MetricLabel>
                                <MetricSub>{s.completed_7d} this week</MetricSub>
                            </MetricCard>
                            <MetricCard>
                                <MetricValue $color="#DC2626">{s.total_failed}</MetricValue>
                                <MetricLabel>Failed</MetricLabel>
                                <MetricSub>{s.failed_7d} this week</MetricSub>
                            </MetricCard>
                            <MetricCard>
                                <MetricValue $color="#2563EB">{s.total_reviewed}</MetricValue>
                                <MetricLabel>Reviewed</MetricLabel>
                                <MetricSub>{s.reviewed_7d} this week</MetricSub>
                            </MetricCard>
                            <MetricCard>
                                <MetricValue $color="#D97706">{s.total_unreviewed}</MetricValue>
                                <MetricLabel>Needs Review</MetricLabel>
                            </MetricCard>
                            <MetricCard>
                                <MetricValue>{s.total_patients}</MetricValue>
                                <MetricLabel>Subjects</MetricLabel>
                            </MetricCard>
                        </MetricsGrid>

                        <TwoCol>
                            {/* Pipeline Health */}
                            <SectionCard>
                                <SectionTitle>Pipeline Health</SectionTitle>
                                <PipelineStatRow>
                                    <StatLabel>Success rate</StatLabel>
                                    <BarBg>
                                        <BarFill $pct={100 - s.failure_rate_pct} $color="#059669" />
                                    </BarBg>
                                    <PipelineStatValue $color="#059669">
                                        {(100 - s.failure_rate_pct).toFixed(1)}%
                                    </PipelineStatValue>
                                </PipelineStatRow>
                                <PipelineStatRow>
                                    <StatLabel>Failure rate</StatLabel>
                                    <BarBg>
                                        <BarFill $pct={s.failure_rate_pct} $color="#DC2626" />
                                    </BarBg>
                                    <PipelineStatValue $color="#DC2626">{s.failure_rate_pct}%</PipelineStatValue>
                                </PipelineStatRow>
                                <PipelineStatRow>
                                    <StatLabel>Review rate</StatLabel>
                                    <BarBg>
                                        <BarFill $pct={s.review_rate_pct} $color="#2563EB" />
                                    </BarBg>
                                    <PipelineStatValue $color="#2563EB">{s.review_rate_pct}%</PipelineStatValue>
                                </PipelineStatRow>
                            </SectionCard>

                            {/* Data Quality */}
                            <SectionCard>
                                <SectionTitle>Data Quality</SectionTitle>
                                <StatRow>
                                    <StatLabel>Scans linked to subjects</StatLabel>
                                    <StatValue $color="#059669">{s.linked_jobs}</StatValue>
                                </StatRow>
                                <StatRow>
                                    <StatLabel>Unlinked scans</StatLabel>
                                    <StatValue $color={s.unlinked_jobs > 0 ? '#D97706' : Colors.extra.dark_gray}>
                                        {s.unlinked_jobs}
                                    </StatValue>
                                </StatRow>
                                <StatRow>
                                    <StatLabel>Completed (24h)</StatLabel>
                                    <StatValue>{s.completed_24h}</StatValue>
                                </StatRow>
                                <StatRow>
                                    <StatLabel>Completed (7d)</StatLabel>
                                    <StatValue>{s.completed_7d}</StatValue>
                                </StatRow>
                                <StatRow>
                                    <StatLabel>Reviewed (7d)</StatLabel>
                                    <StatValue>{s.reviewed_7d}</StatValue>
                                </StatRow>
                            </SectionCard>
                        </TwoCol>
                    </>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default AnalyticsPage;
